import { useMemo } from "react";

import ActionWrapper from "../action-wrapper";
import CustomTooltip from "@components/ui-components/tooltip";
// ** Third Party
import PropTypes from "prop-types";
import classNames from "classnames";
import { generateRandomString } from "@src/utility/helpers/utils";
import size from "lodash/size";

// Filled and tinted variants. `neutral` and `subtle` carry their own border so
// they read as buttons on a white surface without needing `outline`.
const filledColorClasses = {
  primary  : "bg-primary text-white hover:opacity-90",
  secondary: "bg-secondary text-white hover:opacity-90",
  success  : "bg-success text-white hover:opacity-90",
  danger   : "bg-danger text-white hover:opacity-90",
  warning  : "bg-warning text-white hover:opacity-90",
  info     : "bg-info text-white hover:opacity-90",
  dark     : "bg-navy text-white hover:opacity-90",
  neutral  : "border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-light",
  subtle   : "border border-gray-200 bg-light/60 text-gray-900 hover:bg-light"
};

const outlineColorClasses = {
  primary:
    "border border-primary text-primary bg-transparent hover:bg-primary/10",
  secondary:
    "border border-secondary text-secondary bg-transparent hover:bg-secondary/10",
  success:
    "border border-success text-success bg-transparent hover:bg-success/10",
  danger: "border border-danger text-danger bg-transparent hover:bg-danger/10",
  warning:
    "border border-warning text-warning bg-transparent hover:bg-warning/10",
  info: "border border-info text-info bg-transparent hover:bg-info/10",
  dark: "border border-navy text-navy bg-transparent hover:bg-navy/10"
};

const sizeClasses = {
  // `xs` is the table-row action size: dense enough to sit inside a cell
  // without setting the row height.
  xs: "gap-1.5 px-3 py-1.5 text-xs",
  sm: "gap-2 px-3.5 py-2 text-sm",
  md: "gap-2 px-4 py-2 text-sm",
  lg: "gap-2 px-4 py-2.5 text-sm"
};

// Icon and spinner track the label so a small button does not carry a
// full-size glyph.
const iconSizes = {
  xs: 14,
  sm: 18,
  md: 18,
  lg: 18
};

const spinnerClasses = {
  xs: "h-3.5 w-3.5",
  sm: "h-4 w-4",
  md: "h-4 w-4",
  lg: "h-4 w-4"
};

const COLORS = Object.keys(filledColorClasses);

/**
 * The app's single button. Handles the loading spinner, ability gating,
 * tooltips and the link-styled-as-a-button case (`href`), so screens never
 * hand-roll button markup.
 */
const LoadingButton = (props) => {
  const {
    loading = false,
    onClick = () => {},
    color = "primary",
    size: buttonSize = "md",
    text = "",
    loadingText = "",
    className = "",
    disabled = false,
    testId = "",
    icon = "",
    subject = "",
    action = "",
    outline = false,
    block = false,
    id = "",
    tooltip = "",
    type = "button",
    href = "",
    target = "",
    rel = "",
    download = ""
  } = props;

  // Stable for the life of the instance: the tooltip targets this id, so it
  // must not change between renders.
  const tempId = useMemo(
    () => String(generateRandomString(4) + id)
        .replace(/ /g, "_")
        .replace(/([!"#$%&'()*+-_,./:;<=>?@[\\\]^`{|}~])/g, ""),
    [id]
  );

  const Icon = icon;
  const isDisabled = disabled || loading;
  const isLink = Boolean(href);
  const colorClasses =
    (outline ? outlineColorClasses : filledColorClasses)[color] ||
    filledColorClasses.primary;

  // An anchor ignores the `disabled` attribute, so the disabled look and the
  // dead click both hang off aria-disabled instead.
  const elementProps = isLink
    ? {
        href           : isDisabled ? undefined : href,
        target         : target || undefined,
        rel            : rel || (target === "_blank" ? "noopener noreferrer" : undefined),
        download       : download || undefined,
        "aria-disabled": isDisabled || undefined
      }
    : { type, disabled: isDisabled };

  const Element = isLink ? "a" : "button";

  return (
    <ActionWrapper
      subject={subject}
      action={action}
      child={
        <>
          {size(tooltip) ? (
            <CustomTooltip target={`Id-${tempId}`} content={tooltip} />
          ) : null}
          <Element
            {...elementProps}
            id={`Id-${tempId}`}
            data-cy={testId}
            onClick={isDisabled ? undefined : onClick}
            aria-busy={loading || undefined}
            className={classNames(
              "inline-flex items-center justify-center rounded-lg font-medium transition disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50",
              sizeClasses[buttonSize] || sizeClasses.md,
              colorClasses,
              block && "w-full",
              className
            )}
          >
            {loading ? (
              // border-current keeps the spinner legible on every variant,
              // including the light ones.
              <span
                aria-hidden="true"
                className={classNames(
                  "shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent opacity-70",
                  spinnerClasses[buttonSize] || spinnerClasses.md
                )}
              />
            ) : Icon ? (
              <Icon size={iconSizes[buttonSize] || iconSizes.md} />
            ) : null}
            <span>{loading && loadingText ? loadingText : text}</span>
          </Element>
        </>
      }
    />
  );
};

LoadingButton.propTypes = {
  loading    : PropTypes.bool,
  onClick    : PropTypes.func,
  color      : PropTypes.oneOf(COLORS),
  size       : PropTypes.oneOf(Object.keys(sizeClasses)),
  text       : PropTypes.node,
  loadingText: PropTypes.node,
  className  : PropTypes.string,
  disabled   : PropTypes.bool,
  testId     : PropTypes.string,
  // A component, e.g. a lucide icon.
  icon       : PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.string]),
  subject    : PropTypes.string,
  action     : PropTypes.string,
  outline    : PropTypes.bool,
  block      : PropTypes.bool,
  id         : PropTypes.string,
  tooltip    : PropTypes.string,
  type       : PropTypes.oneOf(["button", "submit", "reset"]),
  // Renders an <a> styled as a button; disables via aria-disabled.
  href       : PropTypes.string,
  target     : PropTypes.string,
  rel        : PropTypes.string,
  // With `href`: saves the target under this file name instead of opening it.
  download   : PropTypes.string
};

export default LoadingButton;
